/**
 * @fileoverview Unified Storage Service
 * @description Service layer wrapping storage adapters with version tracking and error handling
 *
 * Features:
 * - Automatic version tracking for optimistic locking
 * - Unified interface for localStorage and SQL storage
 * - Result pattern for error handling
 * - Conflict detection and management
 *
 * @example
 * ```ts
 * const adapter = createStorageAdapter<SurveyData>('survey', authService);
 * const service = new StorageService(adapter);
 *
 * const result = await service.save('survey-123', data);
 * if (!result.success) {
 *   handleError(result.error);
 * }
 * ```
 */

import type {
  IStorageAdapter,
  Result,
  StoredData,
  SaveOptions,
  ListOptions,
  ConflictInfo,
  StorageMetadata,
} from './storage/IStorageAdapter'
import { StorageError, StorageErrorCode } from './storage/IStorageAdapter'

/**
 * Operation result with conflict support
 */
export interface ServiceResult<T> {
  success: boolean
  data?: StoredData<T>
  error?: { code: StorageErrorCode; message: string }
  conflict?: ConflictInfo<T>
}

/**
 * Delete operation result
 */
export interface DeleteResult {
  success: boolean
  error?: { code: StorageErrorCode; message: string }
}

/**
 * Unified Storage Service
 * Wraps storage adapters with version tracking and standardized error handling
 */
export class StorageService<T> {
  private readonly adapter: IStorageAdapter<T>
  private readonly versions: Map<string, number> = new Map()

  /**
   * Create a new StorageService
   * @param adapter Storage adapter (localStorage or SQL)
   */
  constructor(adapter: IStorageAdapter<T>) {
    this.adapter = adapter
  }

  /**
   * Get data from storage
   * Automatically tracks version for future operations
   *
   * @param key Storage key
   * @returns Result with stored data or null if not found
   */
  async get(key: string): Promise<Result<StoredData<T> | null>> {
    try {
      const result = await this.adapter.get(key)

      if (result.success && result.data) {
        // Track version
        this.versions.set(key, result.data.metadata.version)
      }

      return result
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof StorageError
            ? error
            : new StorageError('Get operation failed', StorageErrorCode.UNKNOWN, error),
      }
    }
  }

  /**
   * Save data to storage with automatic version tracking
   *
   * @param key Storage key
   * @param data Data to save
   * @param options Save options (version, user, org)
   * @returns Result with updated data or conflict info
   */
  async save(key: string, data: T, options?: SaveOptions): Promise<ServiceResult<T>> {
    try {
      // Use tracked version if not specified
      const expectedVersion = options?.expectedVersion ?? this.versions.get(key)
      const saveOptions: SaveOptions =
        expectedVersion !== undefined ? { ...options, expectedVersion } : { ...options }

      const result = await this.adapter.save(key, data, saveOptions)

      if (!result.success) {
        // Check if it's a conflict
        const error = result.error

        if (typeof error === 'object' && error !== null && 'currentVersion' in error) {
          // It's a conflict
          return {
            success: false,
            conflict: error as ConflictInfo<T>,
            error: {
              code: StorageErrorCode.CONFLICT,
              message: 'Version conflict detected',
            },
          }
        }

        // Regular error
        return {
          success: false,
          error: {
            code: (error as StorageError)?.code || StorageErrorCode.UNKNOWN,
            message: (error as StorageError)?.message || 'Save operation failed',
          },
        }
      }

      // Update tracked version on success
      this.versions.set(key, result.data.metadata.version)

      return {
        success: true,
        data: result.data,
      }
    } catch (error) {
      return {
        success: false,
        error: {
          code: (error as StorageError)?.code || StorageErrorCode.UNKNOWN,
          message: (error as StorageError)?.message || 'Save operation failed',
        },
      }
    }
  }

  /**
   * Delete data from storage
   *
   * @param key Storage key
   * @param options Delete options (version)
   * @returns Result indicating success or failure
   */
  async delete(key: string, options?: SaveOptions): Promise<DeleteResult> {
    try {
      // Use tracked version if not specified
      const expectedVersion = options?.expectedVersion ?? this.versions.get(key)
      const deleteOptions: SaveOptions =
        expectedVersion !== undefined ? { ...options, expectedVersion } : { ...options }

      const result = await this.adapter.delete(key, deleteOptions)

      if (!result.success) {
        return {
          success: false,
          error: {
            code: StorageErrorCode.UNKNOWN,
            message: 'Delete operation failed',
          },
        }
      }

      // Clear tracked version on success
      this.versions.delete(key)

      return { success: true }
    } catch (error) {
      return {
        success: false,
        error: {
          code: (error as StorageError)?.code || StorageErrorCode.UNKNOWN,
          message: (error as StorageError)?.message || 'Delete operation failed',
        },
      }
    }
  }

  /**
   * List items from storage
   *
   * @param options List options (orgId, userId, pagination, sorting)
   * @returns Result with array of storage metadata
   */
  async list(options?: ListOptions): Promise<Result<StorageMetadata[]>> {
    try {
      return await this.adapter.list(options)
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof StorageError
            ? error
            : new StorageError('List operation failed', StorageErrorCode.UNKNOWN, error),
      }
    }
  }

  /**
   * Check if storage adapter is available
   *
   * @returns True if adapter is available and operational
   */
  async isAvailable(): Promise<boolean> {
    try {
      return await this.adapter.isAvailable()
    } catch {
      return false
    }
  }

  /**
   * Get tracked version for a key
   *
   * @param key Storage key
   * @returns Version number or undefined if not tracked
   */
  getVersion(key: string): number | undefined {
    return this.versions.get(key)
  }

  /**
   * Clear tracked version for a key
   *
   * @param key Storage key
   */
  clearVersion(key: string): void {
    this.versions.delete(key)
  }

  /**
   * Get adapter type
   *
   * @returns Adapter type ('local' or 'sql')
   */
  getAdapterType(): 'local' | 'sql' {
    return this.adapter.type
  }
}
